import { useEffect, useMemo, useState } from "react";
import type { AppConfig } from "../config";
import * as E from "../escrow";
import AdminSection from "./AdminPanel";
import { AddressChip, CopyButton, ExplorerButton, TokenChip, explorerAccountUrl, explorerTxUrl, tokenTicker } from "../ui";

type Props = {
  config: AppConfig;
  address: string;
  isOwner: boolean;
  contractOwner: string;
};

type Wallet = {
  freeBalance: bigint;
  stakedPositions: Array<{ nonce: number; balance: bigint }>;
};

export default function Dashboard({ config, address, isOwner, contractOwner }: Props) {
  const [pairs, setPairs] = useState<E.EscrowPair[] | null>(null);
  const [pairId, setPairId] = useState<number>(1);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [pools, setPools] = useState<Array<{ creator: string; pool: E.Pool }>>([]);
  const [error, setError] = useState<string | null>(null);

  // ── load pair list (defines what tokens each pair uses) ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ps = await E.getAllPairs(config);
        if (alive) {
          setPairs(ps);
          // ensure pairId points to an active pair if current one was removed/inactive
          if (ps.length > 0 && !ps.find((p) => p.id === pairId)) setPairId(ps[0].id);
        }
      } catch (e) {
        if (alive) setError(String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [config, refreshTick]);

  const selectedPair = pairs?.find((p) => p.id === pairId) ?? null;

  // ── load wallet balances for the SELECTED pair's tokens ──
  useEffect(() => {
    let alive = true;
    if (!selectedPair) {
      setWallet(null);
      return;
    }
    (async () => {
      try {
        const [freeBalance, stakedPositions] = await Promise.all([
          E.getFungibleBalance(config, address, selectedPair.freeToken),
          E.getOwnedSftPositions(config, address, selectedPair.stakedToken),
        ]);
        if (alive) setWallet({ freeBalance, stakedPositions });
      } catch (e) {
        if (alive) setError(String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [config, address, selectedPair?.id, refreshTick]);

  // ── pools for selected pair ──
  useEffect(() => {
    let alive = true;
    if (!selectedPair) return;
    (async () => {
      try {
        const creators = await E.getPoolCreators(config, pairId);
        const ps = await Promise.all(
          creators.map(async (c) => ({ creator: c, pool: await E.getPool(config, pairId, c) })),
        );
        if (alive) setPools(ps);
      } catch (e) {
        if (alive) setError(String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [config, pairId, selectedPair?.id, refreshTick]);

  const refresh = () => setRefreshTick((n) => n + 1);

  if (error) {
    return (
      <Shell config={config} address={address}>
        <div className="card">
          <h3>Error loading on-chain data</h3>
          <p className="mono">{error}</p>
        </div>
      </Shell>
    );
  }
  if (!pairs) {
    return (
      <Shell config={config} address={address}>
        <div className="card muted">Loading on-chain data…</div>
      </Shell>
    );
  }

  const ownPool = pools.find((p) => p.creator.toLowerCase() === address.toLowerCase());
  const freeTicker = selectedPair ? tokenTicker(selectedPair.freeToken) : "";
  const stakedTicker = selectedPair ? tokenTicker(selectedPair.stakedToken) : "";

  return (
    <Shell config={config} address={address} isOwner={isOwner}>
      {/* Admin (owner only) */}
      {isOwner && (
        <AdminSection
          cfg={config}
          address={address}
          pairs={pairs}
          onDone={refresh}
        />
      )}

      {/* Pair selector */}
      <div className="row" style={{ marginBottom: "1rem", flexWrap: "wrap" }}>
        {pairs.length === 0 ? (
          <span className="muted">No pairs configured yet.</span>
        ) : (
          pairs.map((p) => {
            const isSel = pairId === p.id;
            const free = tokenTicker(p.freeToken);
            const staked = tokenTicker(p.stakedToken);
            return (
              <button
                key={p.id}
                className={isSel ? "" : "ghost"}
                onClick={() => setPairId(p.id)}
                title={`${p.stakedToken} -> ${p.freeToken}${p.active ? "" : " (paused)"}${p.publicPools ? "" : " (private)"}`}
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
              >
                <strong>{free}</strong>
                <span style={{ opacity: 0.7, fontWeight: 400 }}>← {staked}</span>
                {!p.active && <span className="badge test">paused</span>}
                {!p.publicPools && <span className="badge">private</span>}
              </button>
            );
          })
        )}
        <div style={{ flex: 1 }} />
        {selectedPair && (
          <span className="muted mono" style={{ fontSize: "0.85rem" }}>
            pair fee: {E.bpsToPct(selectedPair.platformFeeBps)} · pair min:{" "}
            {E.fmtAmount(selectedPair.minSwapAmount)} {stakedTicker} (defaults for new pools)
          </span>
        )}
        <button className="ghost" onClick={refresh}>Refresh</button>
      </div>

      {/* Wallet panel — pair-specific */}
      {selectedPair && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          <h3>Your wallet on this pair</h3>
          {!wallet ? (
            <p className="muted">Loading balances…</p>
          ) : (
            <div className="grid mono" style={{ gridTemplateColumns: "180px 1fr", rowGap: "0.35rem" }}>
              <span className="muted">{selectedPair.freeToken}</span>
              <span>
                {E.fmtAmount(wallet.freeBalance)} {freeTicker}
              </span>
              <span className="muted">{selectedPair.stakedToken}</span>
              <span>
                {wallet.stakedPositions.length === 0 ? (
                  <span className="muted">(no positions)</span>
                ) : (
                  wallet.stakedPositions.map((p) => (
                    <div key={p.nonce}>
                      nonce {p.nonce}: {E.fmtAmount(p.balance)} {stakedTicker}
                    </div>
                  ))
                )}
              </span>
            </div>
          )}
        </div>
      )}

      {selectedPair && wallet && (
        <>
          {!selectedPair.active && (
            <div
              className="card"
              style={{
                marginBottom: "1rem",
                background: "rgba(248, 81, 73, 0.08)",
                borderColor: "var(--danger)",
              }}
            >
              <strong style={{ color: "var(--danger)" }}>⏸ Pair paused</strong>
              <p className="muted" style={{ marginTop: "0.3rem", marginBottom: 0, fontSize: "0.9rem" }}>
                The owner has paused this pair. Swaps and new pools are blocked
                until it's resumed. If you already run a pool here you can still
                fund/withdraw/close it normally.
              </p>
            </div>
          )}

          {/* Pool list */}
          <div className="card" style={{ marginBottom: "1rem" }}>
            <h3 style={{ marginBottom: "0.3rem" }}>
              Pools — get {freeTicker} for your {stakedTicker}
            </h3>
            <p className="muted" style={{ marginTop: 0, fontSize: "0.85rem" }}>
              {selectedPair.stakedToken} → {selectedPair.freeToken}
            </p>
            {pools.length === 0 ? (
              <p className="muted">No pools on this pair yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--text-dim)" }}>
                    <th style={{ padding: "0.4rem 0.5rem" }}>Creator</th>
                    <th style={{ padding: "0.4rem 0.5rem" }}>Discount</th>
                    <th style={{ padding: "0.4rem 0.5rem" }}>{freeTicker} available</th>
                    <th style={{ padding: "0.4rem 0.5rem" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {pools.map(({ creator, pool }) => (
                    <PoolRow
                      key={creator}
                      cfg={config}
                      address={address}
                      pair={selectedPair}
                      creator={creator}
                      pool={pool}
                      ownerAddress={contractOwner}
                      isOwner={isOwner}
                      stakedPositions={wallet.stakedPositions}
                      onDone={refresh}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Your pool on this pair */}
          <div className="card">
            <h3>Your pool — supply {freeTicker} liquidity, collect {stakedTicker}</h3>
            {ownPool ? (
              <OwnPoolPanel
                cfg={config}
                address={address}
                pair={selectedPair}
                pool={ownPool.pool}
                onDone={refresh}
              />
            ) : selectedPair.publicPools || isOwner ? (
              <CreatePoolForm
                cfg={config}
                address={address}
                pair={selectedPair}
                freeBalance={wallet.freeBalance}
                onDone={refresh}
              />
            ) : (
              <div className="row" style={{ gap: "0.5rem", alignItems: "flex-start" }}>
                <span style={{ fontSize: "1.2rem" }}>🔒</span>
                <div>
                  <strong>You cannot create a pool on this pair.</strong>
                  <p className="muted" style={{ margin: "0.2rem 0 0", fontSize: "0.9rem" }}>
                    Pair "{freeTicker} ← {stakedTicker}" is private — only the contract owner
                    can run pools here. You can still swap your {stakedTicker} into the pools
                    above when they're available.
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Shell>
  );
}

// ── shell with navbar ──────────────────────────────────────────────────
function Shell({
  config,
  address,
  isOwner,
  children,
}: {
  config: AppConfig;
  address: string;
  isOwner?: boolean;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="main">
        <div
          className="row"
          style={{
            marginBottom: "1rem",
            padding: "0.55rem 0.8rem",
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            fontSize: "0.85rem",
            flexWrap: "wrap",
            gap: "0.6rem",
          }}
        >
          <AddressChip address={config.contractAddress} network={config.network} label="contract" />
        </div>
        {children}
      </div>
    </>
  );
}

// ── Pool row with inline Swap panel ────────────────────────────────────
function PoolRow({
  cfg,
  address,
  pair,
  creator,
  pool,
  ownerAddress,
  isOwner,
  stakedPositions,
  onDone,
}: {
  cfg: AppConfig;
  address: string;
  pair: E.EscrowPair;
  creator: string;
  pool: E.Pool;
  ownerAddress: string;
  isOwner: boolean;
  stakedPositions: Array<{ nonce: number; balance: bigint }>;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [forcing, setForcing] = useState(false);
  const isOwn = creator.toLowerCase() === address.toLowerCase();
  const isOwnerPool = creator.toLowerCase() === ownerAddress.toLowerCase();
  const freeTicker = tokenTicker(pair.freeToken);
  const stakedTicker = tokenTicker(pair.stakedToken);

  async function onForceClose() {
    if (!confirm(`Force-close ${creator.slice(0, 8)}…'s pool? All funds will be returned to them.`)) {
      return;
    }
    setForcing(true);
    try {
      const tx = await E.buildForceClosePool(cfg, address, pair.id, creator);
      const hash = await E.signAndSend(cfg, tx);
      await E.awaitCompleted(cfg, hash);
      onDone();
    } catch (e) {
      alert(String(e));
    } finally {
      setForcing(false);
    }
  }

  return (
    <>
      <tr style={{ borderTop: "1px solid var(--border)" }}>
        <td style={{ padding: "0.5rem", fontFamily: "ui-monospace" }}>
          {creator.slice(0, 8)}…{creator.slice(-6)}
          {isOwn && <span className="badge" style={{ marginLeft: "0.4rem" }}>you</span>}
          {isOwnerPool && !isOwn && (
            <span className="badge" style={{ marginLeft: "0.4rem" }}>owner</span>
          )}
        </td>
        <td style={{ padding: "0.5rem" }}>
          {E.bpsToPct(pool.discountBps)}
          <span className="muted" style={{ fontSize: "0.75rem", marginLeft: "0.4rem" }}>
            (fee {E.bpsToPct(pool.platformFeeBps)} · min {E.fmtAmount(pool.minSwapAmount)} {stakedTicker})
          </span>
        </td>
        <td style={{ padding: "0.5rem" }}>
          {E.fmtAmount(pool.freeBalance)} {freeTicker}
        </td>
        <td style={{ padding: "0.5rem", textAlign: "right" }}>
          {isOwn ? (
            <span className="muted">manage below</span>
          ) : (
            <>
              <button
                onClick={() => setOpen((o) => !o)}
                disabled={!pair.active}
                title={pair.active ? "" : "pair is paused"}
              >
                {open ? "Cancel" : "Swap"}
              </button>
              {isOwner && (
                <button
                  className="ghost"
                  disabled={forcing}
                  onClick={onForceClose}
                  title="Owner emergency wind-down: returns all funds to the creator"
                  style={{ marginLeft: "0.4rem", color: "var(--danger)" }}
                >
                  {forcing ? "…" : "Force close"}
                </button>
              )}
            </>
          )}
        </td>
      </tr>
      {open && !isOwn && (
        <tr>
          <td colSpan={4} style={{ padding: "0.6rem", background: "var(--bg-elev-2)" }}>
            <SwapForm
              cfg={cfg}
              address={address}
              pair={pair}
              poolCreator={creator}
              pool={pool}
              stakedPositions={stakedPositions}
              onDone={() => {
                setOpen(false);
                onDone();
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Swap form ─────────────────────────────────────────────────────────
function SwapForm({
  cfg,
  address,
  pair,
  poolCreator,
  pool,
  stakedPositions,
  onDone,
}: {
  cfg: AppConfig;
  address: string;
  pair: E.EscrowPair;
  poolCreator: string;
  pool: E.Pool;
  stakedPositions: Array<{ nonce: number; balance: bigint }>;
  onDone: () => void;
}) {
  // Pool's snapshot — pool keeps the min/fee it had at creation, even if the
  // pair-level values were changed later.
  const minSwap = pool.minSwapAmount;
  const haveAny = stakedPositions.length > 0;
  const freeTicker = tokenTicker(pair.freeToken);
  const stakedTicker = tokenTicker(pair.stakedToken);
  const [stakedNonce, setStakedNonce] = useState<number>(stakedPositions[0]?.nonce ?? 0);
  const [amountStr, setAmountStr] = useState("");
  const amount = useMemo(() => (amountStr ? E.parseAmount(amountStr) : 0n), [amountStr]);
  const [quote, setQuote] = useState<E.Quote | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  // live quote on amount change
  useEffect(() => {
    let alive = true;
    if (amount <= 0n) {
      setQuote(null);
      return;
    }
    (async () => {
      try {
        const q = await E.getQuote(cfg, pair.id, poolCreator, amount);
        if (alive) setQuote(q);
      } catch (e) {
        if (alive) setErr(String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [cfg, pair.id, poolCreator, amount]);

  async function onSwap() {
    setErr(null);
    setTxHash(null);
    setBusy(true);
    try {
      // 1% slippage tolerance: min_out = payout * 99 / 100
      const minOut = quote ? (quote.payout * 99n) / 100n : 0n;
      const tx = await E.buildSwap(
        cfg, address, pair.id, poolCreator,
        pair.stakedToken, stakedNonce, amount, minOut,
      );
      const hash = await E.signAndSend(cfg, tx);
      setTxHash(hash);
      await E.awaitCompleted(cfg, hash);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  const positionBalance = stakedPositions.find((p) => p.nonce === stakedNonce)?.balance ?? 0n;
  const belowMin = amount > 0n && amount < minSwap;
  const overBalance = amount > positionBalance;
  const exceedsPool = quote && quote.payout + quote.platformFee > pool.freeBalance;
  const canSubmit = !!quote && amount > 0n && !belowMin && !overBalance && !exceedsPool && !busy;

  if (!haveAny) {
    return <p className="muted">You have no {stakedTicker} positions in this wallet — nothing to swap.</p>;
  }

  return (
    <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "0.8rem" }}>
      <div className="col">
        <label className="muted">Use {stakedTicker} position (nonce)</label>
        <select value={stakedNonce} onChange={(e) => setStakedNonce(Number(e.target.value))}>
          {stakedPositions.map((p) => (
            <option key={p.nonce} value={p.nonce}>
              nonce {p.nonce} — {E.fmtAmount(p.balance)} {stakedTicker} available
            </option>
          ))}
        </select>
        <label className="muted">Amount ({stakedTicker})</label>
        <input
          type="text"
          inputMode="decimal"
          value={amountStr}
          placeholder={`min ${E.fmtAmount(minSwap)}`}
          onChange={(e) => setAmountStr(e.target.value)}
        />
        <div className="row" style={{ gap: "0.4rem" }}>
          <button className="ghost" onClick={() => setAmountStr(E.fmtAmount(positionBalance))}>
            Max
          </button>
          <button onClick={onSwap} disabled={!canSubmit}>
            {busy ? "Submitting…" : "Swap"}
          </button>
        </div>
        {belowMin && <p style={{ color: "var(--danger)" }}>Below minimum swap amount</p>}
        {overBalance && <p style={{ color: "var(--danger)" }}>Exceeds your position balance</p>}
        {exceedsPool && <p style={{ color: "var(--danger)" }}>Pool can't cover this size</p>}
        {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
        {txHash && (
          <p className="mono muted">
            tx:{" "}
            <a
              href={`https://${cfg.network === "mainnet" ? "" : `${cfg.network}-`}explorer.multiversx.com/transactions/${txHash}`}
              target="_blank"
              rel="noreferrer"
            >
              {txHash.slice(0, 14)}…
            </a>
          </p>
        )}
      </div>
      <div className="col">
        <h4 style={{ margin: 0 }}>Quote</h4>
        {!quote ? (
          <p className="muted">Enter an amount above.</p>
        ) : (
          <div className="grid mono" style={{ gridTemplateColumns: "1fr auto", rowGap: "0.25rem" }}>
            <span className="muted">You give</span>
            <span>{E.fmtAmount(quote.stakedIn)} {stakedTicker}</span>
            <span className="muted">You receive</span>
            <span style={{ color: "var(--success)" }}>{E.fmtAmount(quote.payout)} {freeTicker}</span>
            <span className="muted">Pool keeps</span>
            <span style={{ color: "var(--pool)" }}>
              {E.fmtAmount(quote.poolMargin)} {freeTicker} ({E.bpsToPct(quote.discountBps - quote.platformFeeBps)})
            </span>
            <span className="muted">Platform fee</span>
            <span style={{ color: "var(--platform)" }}>
              {E.fmtAmount(quote.platformFee)} {freeTicker} ({E.bpsToPct(quote.platformFeeBps)})
            </span>
            <span className="muted" style={{ borderTop: "1px dashed var(--border)", paddingTop: "0.25rem" }}>
              Total discount
            </span>
            <span style={{ borderTop: "1px dashed var(--border)", paddingTop: "0.25rem" }}>
              {E.bpsToPct(quote.discountBps)} ({E.bpsToPct(quote.discountBps - quote.platformFeeBps)} pool + {E.bpsToPct(quote.platformFeeBps)} platform)
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create-pool form ──────────────────────────────────────────────────
function CreatePoolForm({
  cfg,
  address,
  pair,
  freeBalance,
  onDone,
}: {
  cfg: AppConfig;
  address: string;
  pair: E.EscrowPair;
  freeBalance: bigint;
  onDone: () => void;
}) {
  const [discountStr, setDiscountStr] = useState("10");
  const [fundStr, setFundStr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const freeTicker = tokenTicker(pair.freeToken);
  const stakedTicker = tokenTicker(pair.stakedToken);

  const discountBps = Math.round(Number(discountStr || "0") * 100);
  const fundAmount = fundStr ? E.parseAmount(fundStr) : 0n;
  const feePct = pair.platformFeeBps / 100;
  const discountValid = discountBps >= pair.platformFeeBps && discountBps < 10000;
  const overBalance = fundAmount > freeBalance;
  const canSubmit = discountValid && !overBalance && !busy && pair.active;

  async function onCreate() {
    setErr(null);
    setTxHash(null);
    setBusy(true);
    try {
      const tx = await E.buildCreatePool(
        cfg, address, pair.id, pair.freeToken, discountBps, fundAmount,
      );
      const hash = await E.signAndSend(cfg, tx);
      setTxHash(hash);
      await E.awaitCompleted(cfg, hash);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "0.8rem" }}>
      <div className="col">
        <label className="muted">Discount % (immutable — you must close &amp; reopen to change)</label>
        <input
          type="text"
          inputMode="decimal"
          value={discountStr}
          onChange={(e) => setDiscountStr(e.target.value)}
        />
        <p className="muted mono" style={{ fontSize: "0.85rem" }}>
          Must be ≥ {feePct}% (current pair platform fee) and &lt; 100%.
          Your pool will be locked to: <strong>{feePct}% fee</strong>,{" "}
          <strong>{E.fmtAmount(pair.minSwapAmount)} {stakedTicker} min swap</strong> — these don't
          change later even if the pair-level values do.
        </p>
        <label className="muted">Initial {freeTicker} deposit (optional)</label>
        <input
          type="text"
          inputMode="decimal"
          value={fundStr}
          placeholder={`available: ${E.fmtAmount(freeBalance)} ${freeTicker}`}
          onChange={(e) => setFundStr(e.target.value)}
        />
        <button onClick={onCreate} disabled={!canSubmit}>
          {busy ? "Creating…" : "Create pool"}
        </button>
        {!pair.active && <p style={{ color: "var(--danger)" }}>Pair is paused — cannot create pools right now</p>}
        {!discountValid && pair.active && <p style={{ color: "var(--danger)" }}>Invalid discount</p>}
        {overBalance && <p style={{ color: "var(--danger)" }}>Exceeds wallet balance</p>}
        {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
        {txHash && (
          <p className="mono muted">
            tx:{" "}
            <a
              href={`https://${cfg.network === "mainnet" ? "" : `${cfg.network}-`}explorer.multiversx.com/transactions/${txHash}`}
              target="_blank"
              rel="noreferrer"
            >
              {txHash.slice(0, 14)}…
            </a>
          </p>
        )}
      </div>
      <div className="col muted" style={{ fontSize: "0.9rem" }}>
        <p>
          <strong>How it works.</strong> You quote a fixed discount; users swapping {stakedTicker}{" "}
          into your pool get <code>(1 − discount)</code> of nominal in {freeTicker}. Out of every
          swap, the platform takes its {feePct}% fee from your pool — net pool margin = discount −
          fee.
        </p>
        <p>
          You can <code>fundPool</code> more {freeTicker} anytime, <code>claimCollected</code> the{" "}
          {stakedTicker} you've accumulated, and <code>closePool</code> to exit completely.
        </p>
      </div>
    </div>
  );
}

// ── Own-pool management ───────────────────────────────────────────────
function OwnPoolPanel({
  cfg,
  address,
  pair,
  pool,
  onDone,
}: {
  cfg: AppConfig;
  address: string;
  pair: E.EscrowPair;
  pool: E.Pool;
  onDone: () => void;
}) {
  const [fundStr, setFundStr] = useState("");
  const [withdrawStr, setWithdrawStr] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const freeTicker = tokenTicker(pair.freeToken);
  const stakedTicker = tokenTicker(pair.stakedToken);

  async function run(label: string, build: () => Promise<any>) {
    setErr(null);
    setTxHash(null);
    setBusy(label);
    try {
      const tx = await build();
      const hash = await E.signAndSend(cfg, tx);
      setTxHash(hash);
      await E.awaitCompleted(cfg, hash);
      onDone();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "0.8rem" }}>
      <div className="col">
        <div className="grid mono" style={{ gridTemplateColumns: "180px 1fr", rowGap: "0.3rem" }}>
          <span className="muted">Discount</span>
          <span>{E.bpsToPct(pool.discountBps)} (immutable)</span>
          <span className="muted">{freeTicker} available</span>
          <span>{E.fmtAmount(pool.freeBalance)}</span>
          <span className="muted">Pool's fee / min</span>
          <span className="muted" style={{ fontSize: "0.85rem" }}>
            {E.bpsToPct(pool.platformFeeBps)} · {E.fmtAmount(pool.minSwapAmount)} {stakedTicker}
          </span>
        </div>
        <p
          className="muted"
          style={{
            fontSize: "0.85rem",
            background: "var(--bg-elev-2)",
            border: "1px dashed var(--border)",
            borderRadius: 6,
            padding: "0.5rem 0.7rem",
            margin: "0.6rem 0 0",
          }}
        >
          ℹ️ Every swap sends the {stakedTicker} from the buyer directly to <strong>your wallet</strong> —
          the contract never custodies it. Check your {stakedTicker} balance in the wallet panel above.
        </p>

        <label className="muted" style={{ marginTop: "0.6rem" }}>Add {freeTicker} liquidity</label>
        <div className="row">
          <input
            type="text"
            inputMode="decimal"
            value={fundStr}
            onChange={(e) => setFundStr(e.target.value)}
            placeholder="amount"
          />
          <button
            disabled={!fundStr || !!busy}
            onClick={() =>
              run("fund", () =>
                E.buildFundPool(cfg, address, pair.id, pair.freeToken, E.parseAmount(fundStr)),
              )
            }
          >
            {busy === "fund" ? "…" : "Fund"}
          </button>
        </div>

        <label className="muted" style={{ marginTop: "0.4rem" }}>Withdraw {freeTicker}</label>
        <div className="row">
          <input
            type="text"
            inputMode="decimal"
            value={withdrawStr}
            onChange={(e) => setWithdrawStr(e.target.value)}
            placeholder="amount"
          />
          <button
            className="ghost"
            disabled={!withdrawStr || !!busy}
            onClick={() =>
              run("withdraw", () =>
                E.buildWithdrawFree(cfg, address, pair.id, E.parseAmount(withdrawStr)),
              )
            }
          >
            {busy === "withdraw" ? "…" : "Withdraw"}
          </button>
        </div>
      </div>

      <div className="col">
        <p className="muted">
          To change the discount, <strong>close this pool</strong> (you get everything back) then
          create a new one with the new rate.
        </p>
        <button
          className="ghost"
          disabled={!!busy}
          style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
          onClick={() => {
            if (
              confirm(
                `Close pool? You will receive all remaining ${freeTicker} back. ` +
                  `(${stakedTicker} from past swaps is already in your wallet.)`,
              )
            ) {
              run("close", () => E.buildClosePool(cfg, address, pair.id));
            }
          }}
        >
          {busy === "close" ? "…" : "Close pool"}
        </button>

        {err && <p className="mono" style={{ color: "var(--danger)" }}>{err}</p>}
        {txHash && (
          <p className="mono muted">
            tx:{" "}
            <a
              href={`https://${cfg.network === "mainnet" ? "" : `${cfg.network}-`}explorer.multiversx.com/transactions/${txHash}`}
              target="_blank"
              rel="noreferrer"
            >
              {txHash.slice(0, 14)}…
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
